// ws-client.ts - WebSocket client with exponential backoff reconnect.
//
// On connect:
//   1. Fetch a one-time ticket via REST (POST /ws-ticket).
//   2. Open WebSocket with the ticket in the query string.
//   3. Forward incoming events to the registered handler.
//
// On disconnect:
//   1. Exponential backoff (1s, 2s, 4s, 8s, max 30s).
//   2. Reconnect.
//   3. On reconnect, trigger a REST catch-up to fetch missed items.
//
// The client never sends roomSecret or accessToken over WebSocket.
// The ticket is one-time and short-lived (60s).

export interface WSEvent {
  type: 'item-added' | 'item-deleted' | 'pong';
  roomId: string;
  itemId?: string;
  kind?: string;
  createdAt?: string;
}

export type WSEventHandler = (event: WSEvent) => void;
export type ReconnectHandler = () => void;

export class WSClient {
  private ws: WebSocket | null = null;
  private ticketUrl: string;
  private wsBaseUrl: string;
  private roomId: string;
  private accessToken: string;
  private onEvent: WSEventHandler;
  private onReconnect: ReconnectHandler;
  private backoffMs = 1000;
  private maxBackoffMs = 30000;
  private stopped = false;
  private reconnectTimer: number | null = null;

  constructor(
    roomId: string,
    accessToken: string,
    onEvent: WSEventHandler,
    onReconnect: ReconnectHandler,
  ) {
    this.roomId = roomId;
    this.accessToken = accessToken;
    this.onEvent = onEvent;
    this.onReconnect = onReconnect;
    const origin = window.location.origin;
    this.ticketUrl = `${origin}/api/v1/rooms/${roomId}/ws-ticket`;
    this.wsBaseUrl = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.wsBaseUrl += '//' + window.location.host;
  }

  async connect() {
    if (this.stopped) return;
    try {
      const resp = await fetch(this.ticketUrl, {
        method: 'POST',
        headers: { 'X-Room-Access-Token': this.accessToken },
      });
      if (!resp.ok) {
        throw new Error(`ticket fetch failed: ${resp.status}`);
      }
      const data = await resp.json();
      const ticket = data.ticket;
      const wsUrl = `${this.wsBaseUrl}/ws/v1/rooms/${this.roomId}?ticket=${encodeURIComponent(ticket)}`;
      this.ws = new WebSocket(wsUrl);
      this.ws.onmessage = (e) => this.handleMessage(e);
      this.ws.onclose = () => this.handleClose();
      this.ws.onerror = () => this.ws?.close();
    } catch {
      this.scheduleReconnect();
    }
  }

  private handleMessage(e: MessageEvent) {
    try {
      const evt = JSON.parse(e.data) as WSEvent;
      this.onEvent(evt);
    } catch {
      // Ignore malformed messages.
    }
  }

  private handleClose() {
    this.ws = null;
    if (!this.stopped) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    if (this.stopped) return;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
    }
    this.reconnectTimer = window.setTimeout(async () => {
      this.reconnectTimer = null;
      // Reset backoff on successful reconnect.
      await this.connect();
      // Trigger REST catch-up after reconnect.
      this.onReconnect();
    }, this.backoffMs);
    this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs);
  }

  stop() {
    this.stopped = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }
}
